import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { combineAllWith } from '@shared/combineAllWith';
import { readFiles } from '@shared/fileReaderUtils';
import { filterMap } from '@shared/utils/rxjs';
import { catchError, forkJoin, from, map, mergeMap, Observable, of, toArray } from 'rxjs';
import { DepartmentService, IBaseDepartment } from '../department.service';
import { ExcelFileTransfer, xlsxImportError } from '../file-transfer-services/excelFileTransfer.service';
import { IFileDataSchema } from '../file-transfer-services/interfaces';
import { EmployeeService, IMesUser } from '../mesuser.service';
import { ITeamProfile, TeamProfileService } from '../teamProfiles.service';
import { MesEmployeeValidationService } from '../Validation/mesEmployee.service';
import { FailedParse, FailedValidationImport, ImportErrorTypes, ImportResponse, ImportStatus, ITransferService, SuccessFullImport } from './interfaces';

const sheetName = 'Employees';

const MesEmployeeSchema: IFileDataSchema<IMesUserExportData> = {
	name: sheetName,
	properties: {
		alias: { type: 'string', required: true, fileKey: 'Alias' },
		firstName: { type: 'string', required: true, fileKey: 'FirstName' },
		lastName: { type: 'string', required: true, fileKey: 'LastName' },
		isActive: { type: 'boolean', required: true, fileKey: 'IsActive' },
		teamProfile: { type: 'string', required: true, fileKey: 'TeamProfile' },
		department: { type: 'string', required: true, fileKey: 'Department' },
		firstWorkDate: { type: 'string', required: false, fileKey: 'FirstWorkDate' },
	},
};

export interface MesEmployeeImportContext {
	employees: IMesUser[];
	teamProfiles: ITeamProfile[];
	departments: IBaseDepartment[];
}

@Injectable({
	providedIn: 'root',
})
export class MesEmployeeTransferService implements ITransferService<IMesUser> {
	constructor(
		private employeeService: EmployeeService,
		private teamprofileService: TeamProfileService,
		private departmentService: DepartmentService,
		private mesEmployeeValidationService: MesEmployeeValidationService,
		private excelFileTransfer: ExcelFileTransfer<IMesUserExportData>,
		private translator: TranslateService,
	) {}

	public hasTemplate = () => false;
	public getTemplate = (): void => {
		throw new Error('Employees offer no import template; check hasTemplate() before calling getTemplate().');
	};

	public exportData(user: IMesUser[]) {
		return from(user).pipe(
			combineAllWith(forkJoin([this.teamprofileService.getTeamProfiles(), this.departmentService.getDepartments()])),
			map(([data, [teams, departments]]) => {
				const teamProfileAlias = teams.result.find((t) => t.id === data.teamProfileId)?.alias || '';
				const departmentAlias = departments.result.find((t) => t.id === data.departmentId)?.alias || '';

				return {
					alias: data.alias,
					firstName: data.firstName,
					lastName: data.lastName,
					isActive: data.isActive,
					teamProfile: teamProfileAlias,
					department: departmentAlias,
					firstWorkDate: data.firstWorkDate || '',
				} as IMesUserExportData;
			}),
			toArray(),
			mergeMap((data) => {
				return this.excelFileTransfer.exportData(sheetName, data, MesEmployeeSchema);
			}),
			map(() => user), // Return the original user array after export
			catchError((error) => {
				throw new Error(`Failed to export data: ${error.message}`);
			}),
		);
	}

	importData(file: File | File[], overwrite: boolean = false) {
		const ctx: MesEmployeeImportContext = {
			employees: [],
			teamProfiles: [],
			departments: [],
		};

		const ImportErrors: ImportErrorTypes[] = [];

		if (!Array.isArray(file)) {
			// If a single file is provided, we convert it to an array
			file = [file];
		}

		const ob = readFiles(file, 'binary').pipe(
			mergeMap(this.importFileData(ImportErrors)),
			combineAllWith(
				forkJoin([this.employeeService.getEmployees(), this.teamprofileService.getTeamProfiles(), this.departmentService.getDepartments()]).pipe(
					map(([employeesData, teamProfilesData, departmentsData]) => {
						[ctx.employees, ctx.teamProfiles, ctx.departments] = [employeesData.result, teamProfilesData.result, departmentsData.result];
						return {}; // We don't need to emit anything from this observable
					}),
				),
			),
			map(([user]) => user),
			filterMap(this.validateAndMapAliasesUser(ctx, ImportErrors)),
			filterMap(this.validateUser(ImportErrors)),
			mergeMap(this.createOrUpdateUser(ctx, overwrite, ImportErrors)),
			toArray(),
			map((users) => {
				const successfulImports = users.map((u) => ({ Id: u.entity.alias, status: u.status }) as SuccessFullImport);

				return {
					successfulImports,
					errors: ImportErrors,
				} as ImportResponse;
			}),
		);

		return ob;
	}

	importFileData(ImportErrors: ImportErrorTypes[]) {
		return (buffer: ArrayBuffer): Observable<IMesUserExportData> => {
			const errorList: xlsxImportError[] = [];

			try {
				const rows = this.excelFileTransfer.importData(buffer, MesEmployeeSchema, errorList).map((imported) => imported.data);

				if (errorList.length > 0) {
					// If there are errors, we throw an error with the list of errors
					errorList.forEach((e) => {
						ImportErrors.push({
							message: e.error,
							status: ImportStatus.ParsingError,
						} as FailedParse);
					});
				}

				return from(rows);
			} catch (error) {
				ImportErrors.push({
					status: ImportStatus.ParsingError,
					message: error.message,
				} as FailedParse);
				return of();
			}
		};
	}

	validateAndMapAliasesUser(ctx: MesEmployeeImportContext, ImportErrors: ImportErrorTypes[]) {
		return (user: IMesUserExportData): IMesUser | undefined => {
			const departmentMatch = ctx.departments.find((d) => d.alias === user.department);
			const teamProfileMatch = ctx.teamProfiles.find((t) => t.alias === user.teamProfile);

			if (!departmentMatch || !teamProfileMatch) {
				const error: FailedValidationImport = {
					Id: `${user.alias} (${user.firstName} ${user.lastName})`,
					status: ImportStatus.ValidationError,
					errors: {},
				};

				if (!departmentMatch) {
					const message = this.translator.instant('mes.administration.users.import.errors.department-not-found', { department: user.department });
					error.errors['department'] = { required: message };
				}

				if (!teamProfileMatch) {
					const message = this.translator.instant('mes.administration.users.import.errors.teamProfile-not-found', { teamProfile: user.teamProfile });
					error.errors['teamProfile'] = { required: message };
				}

				ImportErrors.push(error);

				return undefined;
			}

			// Validate and map firstWorkDate
			let firstWorkDate: string | null = null;
			if (user.firstWorkDate && user.firstWorkDate.trim() !== '') {
				const dateValue = user.firstWorkDate.trim();
				if (!this.isValidDate(dateValue)) {
					ImportErrors.push({
						message: this.translator.instant('mes.administration.users.import.errors.invalid-date', { value: dateValue, alias: user.alias }),
						status: ImportStatus.ParsingError,
					} as FailedParse);
					return undefined;
				}
				firstWorkDate = dateValue;
			}

			return {
				alias: user.alias,
				// the salaryCode has been chosen to be the alias
				salaryCode: user.alias,
				firstName: user.firstName,
				lastName: user.lastName,
				isActive: user.isActive,
				teamProfileId: teamProfileMatch.id,
				departmentId: departmentMatch.id,
				firstWorkDate,
			} as IMesUser;
		};
	}

	private isValidDate(value: string): boolean {
		// Check regex pattern yyyy-mm-dd
		const datePattern = /^\d{4}-\d{2}-\d{2}$/;
		if (!datePattern.test(value)) {
			return false;
		}

		// Check that it represents a valid calendar date
		const [yearStr, monthStr, dayStr] = value.split('-');
		const year = Number(yearStr);
		const month = Number(monthStr);
		const day = Number(dayStr);

		// Month must be 1-12, day must be 1-31 (basic range check)
		if (month < 1 || month > 12 || day < 1 || day > 31) {
			return false;
		}

		// Use Date to validate the actual calendar date
		const date = new Date(year, month - 1, day);
		return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
	}

	validateUser(ImportErrors: ImportErrorTypes[]) {
		return (user: IMesUser): IMesUser | undefined => {
			// Validate user
			const validationErrors = this.mesEmployeeValidationService.validateUser(user, false);
			if (Object.keys(validationErrors).length > 0) {
				ImportErrors.push({
					Id: `${user.alias} (${user.firstName} ${user.lastName})`,
					status: ImportStatus.ValidationError,
					errors: validationErrors,
				});
				return undefined;
			}

			return user;
		};
	}

	createOrUpdateUser(ctx: MesEmployeeImportContext, overwrite: boolean, ImportErrors: ImportErrorTypes[]) {
		return (user: IMesUser) => {
			const existingUser = ctx.employees.find((e) => e.alias === user.alias);
			if (existingUser) {
				if (!overwrite) {
					const message = this.translator.instant('mes.administration.users.import.errors.exists', { alias: user.alias });
					// Do not overwrite existing user
					ImportErrors.push({
						Id: `${user.alias} (${user.firstName} ${user.lastName})`,
						status: ImportStatus.Skipped,
						message,
					});
					return of();
				}
				// preserve the id of the existing user
				user.id = existingUser.id;
				// Update existing user
				return this.employeeService.updateEmployee(user).pipe(
					map(() => ({
						entity: user,
						status: ImportStatus.SuccessUpdate,
					})),

					catchError((error) => {
						const message = this.translator.instant('mes.administration.users.import.errors.update-failed', { alias: user.alias, msg: error.message });
						ImportErrors.push({
							Id: user.alias,
							status: ImportStatus.Failed,
							message,
						});
						return of();
					}),
				);
			} else {
				// Create new user
				return this.employeeService.createEmployee(user).pipe(
					map(() => ({
						entity: user,
						status: ImportStatus.SuccessCreate,
					})),

					catchError((error) => {
						const message = this.translator.instant('mes.administration.users.import.errors.create-failed', { alias: user.alias, msg: error.message });
						ImportErrors.push({
							Id: user.alias,
							status: ImportStatus.Failed,
							message,
						});
						return of();
					}),
				);
			}
		};
	}
}

export interface IMesUserExportInfo {
	// Define any additional properties needed for export
	// For example, you might want to include department or team information
	user: IMesUser;
	departmentAlias: string;
	teamProfileAlias: string;
}

export interface IMesUserExportData {
	alias: string;
	firstName: string;
	lastName: string;
	isActive: boolean;
	teamProfile: string;
	department: string;
	firstWorkDate: string;
}
